import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Radio,
  Drawer,
  Table,
  DatePicker,
  Spin,
  Select,
} from "antd";
import { EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import {
  useCreateTeamMemberMutation,
  useDeleteTeamMemberMutation,
  useFetchTeamMembersQuery,
  useUpdateTeamMemberMutation,
} from "./team.service";
import { toast } from "react-toastify";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";
import "./style.css";
import { useNavigate } from "react-router-dom";
import { trainingLevel } from "../../constant";

const { Column } = Table;

const options = [
  {
    value: "level1",
    label: "Level 1",
  },
  {
    value: "level2",
    label: "Level 2",
  },
  {
    value: "level3",
    label: "Level 3",
  },
];

export default function Team() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [isDrawer, setIsDrawer] = useState(false);
  const [isToolLoading, setIsToolLoading] = useState("");
  const [isEditable, setIsEditable] = useState();

  const { data = [], isLoading } = useFetchTeamMembersQuery();
  const [requestCreateTeamMember, { isSuccess, isLoading: isCreateLoading }] =
    useCreateTeamMemberMutation();
  const [requestUpdateTeamMember, { isSuccess: isUpdateSuccess }] =
    useUpdateTeamMemberMutation();

  const [requestDeleteTeamMember, { isSuccess: isDeleteSuccess }] =
    useDeleteTeamMemberMutation();

  const handleDrawer = () => {
    setIsDrawer(true);
  };

  const handleDeleteTeamMember = async (id) => {
    setIsToolLoading(id);
    await requestDeleteTeamMember(id);
    setIsToolLoading("");
  };

  const handleUpdateTeamMember = (data) => {
    setIsEditable(data._id);
    setIsDrawer(true);
    form.setFieldsValue({
      name: data.name,
      dateOfBirth: moment(data.dateOfBirth),
      email: data.email,
      mobileNumber: data.mobileNumber,
      trainingLevel: data.trainingLevel,
      hygieneTraining: data.hygieneTraining,
      allergenTraining: data.allergenTraining,
    });
  };

  useEffect(() => {
    if (!isDrawer) {
      form.resetFields();
      setIsEditable();
    }
  }, [isDrawer]);

  useEffect(() => {
    if (isSuccess) {
      // Removed toast.success("team member created successfully");
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isUpdateSuccess) {
      // Removed toast.success("team member updated successfully");
    }
  }, [isUpdateSuccess]);

  useEffect(() => {
    if (isDeleteSuccess) {
      // Removed toast.success("team member deleted successfully");
    }
  }, [isDeleteSuccess]);

  const handleFormSubmit = (data) => {
    if (isEditable)
      requestUpdateTeamMember({
        id: isEditable,
        data: { ...data, userLevel: "level1" },
      });
    else requestCreateTeamMember({ ...data, userLevel: "level1" });
    setIsDrawer(false);
  };

  const handleView = (id) => {
    navigate("/team/view/" + id);
  };

  return (
    <Card
      className="team-page"
      bordered={false}
      title={t("TEAM MEMBERS LIST")}
      extra={
        <Button type="primary" onClick={handleDrawer}>
          {t("add new team member")}
        </Button>
      }
    >
      <Spin spinning={isLoading}>
        <div className="team-page-container">
          <div className="team-page-container-content">
            <Table
              dataSource={data}
              pagination={{
                current: 1,
                pageSize: 10,
              }}
              // loading={loading}
            >
              <Column align="center" title={t("NAME")} dataIndex={"name"} />
              <Column
                align="center"
                title={t("EMAIL ADDRESS")}
                dataIndex={"email"}
              />
              <Column
                align="center"
                title={t("TRAINING LEVEL")}
                dataIndex={"trainingLevel"}
                render={(level) => trainingLevel[level]}
              />
              <Column
                title={t("TOOLS")}
                align="center"
                render={(value, record, index) => (
                  <>
                    <Button
                      className="team-page-container-content-tool"
                      type="default"
                      variant="solid"
                      color="cyan"
                      icon={<EyeOutlined />}
                      onClick={() => handleView(record._id)}
                      size="small"
                    >
                      {t("View")}
                    </Button>

                    <Button
                      className="team-page-container-content-tool"
                      color="green"
                      variant="solid"
                      icon={<EditOutlined />}
                      size="small"
                      isLoading={isToolLoading === record._id ? true : false}
                      onClick={() => handleUpdateTeamMember(record)}
                    >
                      {t("Edit")}
                    </Button>
                    <Button
                      className="team-page-container-content-tool"
                      variant="solid"
                      color="danger"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteTeamMember(record._id)}
                      isLoading={isToolLoading === record._id ? true : false}
                    >
                      {t("DELETE")}
                    </Button>
                  </>
                )}
              />
            </Table>
          </div>

          <Drawer
            title={t("Add new team member")}
            width={"70%"}
            open={isDrawer}
            onClose={() => {
              form.resetFields();
              setIsDrawer(false);
            }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFormSubmit}
              initialValues={{
                name: "",
                birthday: "",
                email: "",
                mobile_number: "",
                user_level: "",
                training_level: "",
                hygiene_training: "",
                allergen_training: "",
              }}
            >
              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("NAME")}
                </div>
                <div className="team-page-container-form-input">
                  <Form.Item
                    name="name"
                    className="team-page-container-form-input-width"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a name",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </div>
              </div>
              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("DATE OF BIRTH")}
                </div>
                <div className="team-page-container-form-input">
                  <Form.Item
                    name="dateOfBirth"
                    className="team-page-container-form-input-width"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a birthday",
                      },
                    ]}
                  >
                    <DatePicker
                      placeholder=""
                      style={{ width: "100%" }}
                      format={"YYYY-MM-DD"}
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("EMAIL")}
                </div>
                <div className="team-page-container-form-input">
                  <Form.Item
                    name="email"
                    className="team-page-container-form-input-width"
                    rules={[
                      {
                        required: true,
                        message: "Please enter an email",
                        type: "email",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </div>
              </div>
              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("MOBILE NUMBER")}
                </div>
                <div className="team-page-container-form-input">
                  <Form.Item
                    name="mobileNumber"
                    className="team-page-container-form-input-width"
                    rules={[
                      {
                        required: true,
                        message: "Please enter an mobile number",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </div>
              </div>
              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("TRAINING LEVEL")}
                </div>
                <div className="team-page-container-form-input">
                  <Form.Item
                    name="trainingLevel"
                    className="team-page-container-form-input-width"
                    rules={[
                      {
                        required: true,
                        message: "Please enter an training level",
                      },
                    ]}
                  >
                    <Select options={options} />
                  </Form.Item>
                </div>
              </div>
              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("HYGIENE TRAINING")}
                </div>
                <div className="team-page-container-form-input team-page-container-form-input-radio">
                  <Form.Item
                    name="hygieneTraining"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a hygiene training",
                      },
                    ]}
                  >
                    <Radio.Group
                      options={[
                        {
                          label: t("YES"),
                          value: true,
                        },
                        {
                          label: t("NO"),
                          value: false,
                        },
                      ]}
                      optionType="button"
                      buttonStyle="solid"
                    />
                  </Form.Item>
                  <div className="team-page-container-form-input-tag">
                    {t("SEND FORMS")}
                  </div>
                </div>
              </div>
              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("ALLERGEN TRAINING")}
                </div>
                <div className="team-page-container-form-input team-page-container-form-input-radio">
                  <Form.Item
                    name="allergenTraining"
                    rules={[
                      {
                        required: true,
                        message: "Please enter an allergen training",
                      },
                    ]}
                  >
                    <Radio.Group
                      options={[
                        {
                          label: t("YES"),
                          value: true,
                        },
                        {
                          label: t("NO"),
                          value: false,
                        },
                      ]}
                      optionType="button"
                      buttonStyle="solid"
                    />
                  </Form.Item>
                  <div className="team-page-container-form-input-tag">
                    {t("SEND FORMS")}
                  </div>
                </div>
              </div>

              <div className="team-page-container-form-button">
                <Button type="primary" htmlType="submit">
                  {isEditable ? t("UPDATE") : t("CREATE")}
                </Button>
              </div>
            </Form>
          </Drawer>
        </div>
      </Spin>
    </Card>
  );
}
